/**
 * TypeScript mirrors of the Admin API's JSON response/request shapes.
 *
 * Source of truth (read, not guessed): `AdminHttpResponses.cs` serializes
 * with `JsonSerializerDefaults.Web` (camelCase) + `JsonStringEnumConverter`
 * (enums serialize as their C# member name, not an int) — every field name
 * and enum value string below is copied 1:1 from
 * `src/Orchestrator/Models/Contact.cs` and
 * `src/Orchestrator/Services/Admin/AdminContactModels.cs` (PR 4a/5).
 *
 * This file is intentionally organized by backend resource (Contacts today;
 * Segments/Templates/Campaigns/Telemetry sections are reserved below for
 * PR 6b — task 6.1-6.4 scope is contacts only, see tasks.md Phase 6) so it
 * can be extended in place rather than replaced.
 */

// ---------------------------------------------------------------------------
// Shared / cross-resource
// ---------------------------------------------------------------------------

/** Generic paged-list envelope — matches every `admin/...?page=&size=` list route. */
export interface AdminPage<T> {
  items: T[];
  page: number;
  size: number;
  total: number;
}

/** Uniform error body written by `AdminHttpResponses.Error` (`{"error":"..."}`) and by `AdminAuthMiddleware`'s 401/403 short-circuit. */
export interface AdminApiError {
  error: string;
}

// Typed HTTP error taxonomy (task 2.1/2.2). Backend evidence
// (`Functions/Admin/*.cs`): validation always responds `400 BadRequest` with
// the same `{"error":"..."}` shape as every other status — no separate
// `422`, so `400` maps to `"validation"` here.
export type HttpErrorKind =
  | "auth"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "validation"
  | "network"
  | "server"
  | "unknown";

/** Pure HTTP-status classifier reused by `api/client.ts` to attach `kind` to every thrown error. */
export function classifyHttpStatus(status: number): HttpErrorKind {
  switch (status) {
    case 400:
      return "validation";
    case 401:
      return "auth";
    case 403:
      return "forbidden";
    case 404:
      return "notFound";
    case 409:
      return "conflict";
    default:
      return status >= 500 && status < 600 ? "server" : "unknown";
  }
}

// Operational metric capability model (design.md section 7) — one transport
// shape for every supported/blocked outcome instead of `number | null`.
export type MetricAvailability = "available" | "unavailable";

/**
 * Mirrors the backend `OperationalMetric` record (design.md section 8 DTO contracts).
 * `source: "contact"` (issue #354) marks an `AdminOverview.accountMetrics` entry —
 * a current-state count (e.g. `suprimidos`, from `contact.estado`) that is never
 * `send_event`-derived and never bound to a time window.
 */
export interface OperationalMetric {
  key: string;
  availability: MetricAvailability;
  value: number | null;
  source: "campaign" | "send_event" | "segment-current-membership" | "capability-catalog" | "contact";
  observedAt: string;
  blockedBy: "F2-01" | null;
}

/** Invariant (design.md section 7): `available` => numeric value + no blocker; `unavailable` => `value: null` + non-empty blocker. UI must branch on `availability`, never value/null truthiness alone. */
export function isValidOperationalMetric(metric: OperationalMetric): boolean {
  if (metric.availability === "available") {
    return typeof metric.value === "number" && metric.blockedBy === null;
  }
  return metric.value === null && metric.blockedBy !== null;
}

// ---------------------------------------------------------------------------
// Overview (`admin/panel/overview` — Services/Admin/AdminOverviewModels.cs)
// ---------------------------------------------------------------------------

/** Mirrors `OverviewScope` — bounds `AdminOverview` so the SPA never treats it as an unbounded total (design.md §8). */
export interface OverviewScope {
  eventWindowStart: string;
  recentLimit: number;
  priorityLimit: number;
}

/** Mirrors `CampaignStateCount` — one of the 7 fixed lifecycle states, always measured (never F2-01-blocked). */
export interface CampaignStateCount {
  state: string;
  count: number;
}

/** Mirrors `AdminPriorityItem`. `campaignId`/`campaignName` are nullable — a priority row need not always name a single campaign. */
export interface AdminPriorityItem {
  kind: string;
  severity: string;
  campaignId: string | null;
  campaignName: string | null;
  count: number;
  source: string;
  observedAt: string;
}

/**
 * Mirrors `AdminOverviewRecentCampaign` — a lighter, differently-shaped
 * record than `AdminCampaignSummary` above (no `asunto`/`fechaProgramada`,
 * has `templateId`/`templateName`/`segmentId`/`segmentName` instead). Do not
 * conflate the two: `GET panel/campaigns` and the overview's recent-campaigns
 * result set are two distinct contracts.
 */
export interface AdminOverviewRecentCampaign {
  campaignId: string;
  nombre: string;
  estado: string;
  updatedAt: string;
  templateId: string;
  templateName: string;
  segmentId: string;
  segmentName: string;
}

/**
 * `GET panel/overview` response — mirrors `AdminOverview`. There is
 * deliberately no `capabilities` field: PR3/PR4 documented this omission
 * (`AdminOverviewModels.cs`) because the "which capability is
 * supported/blocked" content is already fully expressed by `metrics`
 * (design.md §8's example DTO shows a `Capabilities` field that this backend
 * never actually shipped for this contract — read the real DTO, not the
 * design sketch, when in doubt).
 *
 * `metricsAllTime` (issue #353) and `accountMetrics` (issue #354) are
 * additive backend fields, now consumed by `TelemetryScreen`'s "Resumen de
 * cuenta" (PR #352) — required here, no longer optional, since a fixture
 * missing either would mean the test doesn't reflect the real (always
 * present) response shape. `metricsAllTime` mirrors `metrics` (same F2-01
 * send_event catalog, no 7-day window). `accountMetrics` is a SEPARATE,
 * smaller catalog of current-state counts (e.g. `suprimidos`,
 * `source: "contact"`) — never merged into `metrics`/`metricsAllTime` (see
 * `AdminOverview.AccountMetrics` backend doc for the invariant conflict
 * this avoided).
 */
export interface AdminOverview {
  generatedAt: string;
  scope: OverviewScope;
  campaignStates: CampaignStateCount[];
  metrics: OperationalMetric[];
  priorities: AdminPriorityItem[];
  recentCampaigns: AdminOverviewRecentCampaign[];
  metricsAllTime: OperationalMetric[];
  accountMetrics: OperationalMetric[];
}

// ---------------------------------------------------------------------------
// Telemetry (`GET panel/telemetry/...` — Services/Admin/AdminTelemetry*Models.cs)
// ---------------------------------------------------------------------------

/**
 * `GET panel/telemetry/daily?days=` response — mirrors `AdminTelemetryDailySeries`
 * (issue #355). One flat row per day×tipo — the backend never pivots into a
 * date×type matrix ("el frontend pivotea si hace falta, el backend no arma
 * una matriz", per the issue itself); only `tipo`s in the available catalog
 * ever appear, never a pending/blocked one. `fecha`/`windowStart` are plain
 * `yyyy-MM-dd` (C# `DateOnly`), not a full ISO instant.
 */
export interface AdminTelemetryDailyPoint {
  fecha: string;
  tipo: string;
  conteo: number;
}

export interface AdminTelemetryDailySeries {
  generatedAt: string;
  windowStart: string;
  days: number;
  points: AdminTelemetryDailyPoint[];
}

/**
 * `GET panel/telemetry/breakdown?groupBy=template|show` response — mirrors
 * `AdminTelemetryBreakdown`/`AdminTelemetryBreakdownGroup` (issue #356).
 * `id`/`nombre` both `null` together means "Sin Show asociado" (campaigns
 * with no `campaign_audience_filter` row, or `show_id IS NULL`) — a REAL
 * group the backend never drops, must render explicitly, never filter out.
 * `metrics` is the full 8-entry catalog per group (available AND blocked),
 * same shape as `AdminOverview.metrics` — a group with zero events of an
 * otherwise-available type still shows a real measured zero for it. A
 * template/Show with ZERO events across every type never generates a group
 * at all (disclosed backend limitation, not a frontend bug if one seems
 * "missing"). All-time scope, same as `metricsAllTime` — no 7-day window.
 */
export interface AdminTelemetryBreakdownGroup {
  id: string | null;
  nombre: string | null;
  metrics: OperationalMetric[];
}

export type AdminTelemetryGroupBy = "template" | "show";

export interface AdminTelemetryBreakdown {
  generatedAt: string;
  groupBy: AdminTelemetryGroupBy;
  groups: AdminTelemetryBreakdownGroup[];
}

// ---------------------------------------------------------------------------
// Contacts (`admin/contacts...` — Models/Contact.cs, Services/Admin/AdminContactModels.cs)
// ---------------------------------------------------------------------------

/** Mirrors the `HierarchyLevel` enum (Models/Contact.cs) — serialized as text. */
export type HierarchyLevel = "CLevel" | "Director" | "Gerente" | "Coordinador" | "Otro";

/** Mirrors the `ContactStatus` enum (Models/Contact.cs) — serialized as text. */
export type ContactStatus = "Activo" | "Suprimido" | "EnNegociacion";

/** Mirrors the `Contact` record (Models/Contact.cs). */
export interface Contact {
  contactId: string;
  clientId: string;
  email: string;
  nombre: string;
  empresa: string;
  cargo: string;
  nivelJerarquico: HierarchyLevel;
  industria: string | null;
  zonaHoraria: string;
  estado: ContactStatus;
  /**
   * issue #274: nombres de TODOS los shows del contacto, concatenados
   * ("Show_Europa, jhamf_prueba") -- decision de producto (usuario,
   * 2026-09-23). `null` si el contacto no pertenece a ningun Show. Solo
   * `GET panel/contacts` (AdminContactSqlBuilder.BuildListQuery) puebla este
   * campo hoy.
   */
  shows: string | null;
}

/** `GET admin/contacts?...` response — mirrors `AdminContactPage`. */
export type AdminContactPage = AdminPage<Contact>;

/** Mirrors `AdminActiveSuppression`. */
export interface AdminActiveSuppression {
  motivo: string;
  createdAt: string;
}

/** Mirrors `AdminSuppressionAuditEntry` — one `suppression_audit` row. */
export interface AdminSuppressionAuditEntry {
  auditId: string;
  accion: "suprimir" | "revertir";
  motivo: string | null;
  actorOid: string;
  actorUpn: string;
  actorRol: string;
  timestamp: string;
}

/** `GET admin/contacts/{id}` response — mirrors `AdminContactDetail`. */
export interface AdminContactDetail {
  contact: Contact;
  activeSuppression: AdminActiveSuppression | null;
  auditHistory: AdminSuppressionAuditEntry[];
}

/** Query params accepted by `GET admin/contacts` (mirrors `AdminContactQuery`, camelCase query keys per `AdminContactsFunction.List`). */
export interface AdminContactListParams {
  q?: string;
  estado?: string;
  nivelJerarquico?: string;
  industria?: string;
  /** issue #274: mirrors `AdminContactQuery.ShowId` (a GUID) -- travels as-is, no casing/spelling conversion (unlike estado/nivelJerarquico). */
  showId?: string;
  page?: number;
  size?: number;
}

/** `POST admin/contacts/{id}/suppress` request body — mirrors `SuppressContactRequest`. */
export interface SuppressContactRequest {
  motivo: string;
}

/**
 * `POST admin/contacts/import` request row — mirrors `AdminContactImportRow`
 * (AdminContactModels.cs). No `clientId` member on purpose: the server
 * structurally ignores any `clientId` sent here (D3/ADR-005).
 */
export interface AdminContactImportRow {
  email: string;
  nombre: string;
  empresa: string;
  cargo: string;
  nivelJerarquico: string;
  industria?: string;
  zonaHoraria?: string;
  estado?: string;
}

/** Mirrors the `AdminImportRowOutcome` enum (AdminContactModels.cs) — serialized as text. */
export type AdminImportRowOutcome = "Insertado" | "DuplicadoEnArchivo" | "DuplicadoEnBd" | "Rechazado";

/** Mirrors `AdminContactImportRowResult` — one row's outcome, `fila` is the 0-based index in the submitted array. */
export interface AdminContactImportRowResult {
  fila: number;
  resultado: AdminImportRowOutcome;
  motivo: string | null;
}

/**
 * `POST admin/contacts/import` response — mirrors `AdminContactImportResult`.
 * `interrumpido` (true only if a bulk-insert chunk failed mid-way) MUST be
 * surfaced distinctly from a normal completed import — a partial import is
 * not the same as "done".
 */
export interface AdminContactImportResult {
  insertados: number;
  omitidosDuplicados: number;
  rechazados: AdminContactImportRowResult[];
  filas: AdminContactImportRowResult[];
  interrumpido: boolean;
}

/**
 * `GET panel/show-options` response item — mirrors `AdminShowOption`
 * (AdminInterfaces.cs). Bounded (`size<=50`) selector read (epic #236 task
 * #238, work unit 4): lets the contact-import panel require a Show before
 * submit, same "options" pattern as `AdminTemplateOption`/segment options.
 * No `clientId` member — the list is already server-scoped to the caller's
 * tenant (D3/ADR-005).
 */
export interface AdminShowOption {
  showId: string;
  nombre: string;
}

// ---------------------------------------------------------------------------
// Segments (`admin/segments...` — Services/Admin/AdminSegmentModels.cs)
// ---------------------------------------------------------------------------

/**
 * Mirrors `SegmentCriteria` (AdminSegmentModels.cs): today documents exactly
 * two keys of `segment.criterio` JSONB — `nivelJerarquico` (list, null/absent
 * = no filter) and `industria` (single value, null/absent = no filter).
 */
export interface SegmentCriteria {
  nivelJerarquico: string[] | null;
  industria: string | null;
}

/** `GET admin/segments`/`admin/segments/{id}` response item — mirrors `AdminSegmentSummary`. */
export interface AdminSegmentSummary {
  segmentId: string;
  nombre: string;
  criterio: SegmentCriteria;
  memberCount: number;
}

/** `POST admin/segments` (crear) / `PUT admin/segments/{id}` (editar) request body — mirrors `AdminSegmentWriteRequest` (panel-segment-crud). */
export interface AdminSegmentWriteRequest {
  nombre: string;
  criterio: SegmentCriteria;
}

/** `POST admin/segments/{id}/contacts` request. */
export interface AddSegmentContactsRequest {
  contactIds: string[];
}

/** `POST admin/segments/{id}/contacts` response. */
export interface AddSegmentContactsResult {
  added: number;
  alreadyIncluded: number;
  memberCount: number;
}

// ---------------------------------------------------------------------------
// Templates (`admin/templates...` — Services/Admin/AdminTemplateModels.cs)
// ---------------------------------------------------------------------------

/** `GET admin/templates` response item — mirrors `AdminTemplateSummary`. */
export interface AdminTemplateSummary {
  templateId: string;
  nombre: string;
  producto: string;
  temporada: string;
  version: number;
  rutaBlob: string;
  activo: boolean;
}

/** `GET admin/templates/{id}/preview` response — mirrors the anonymous `{ html }` shape from `AdminTemplatesFunction.GetPreview`. */
export interface AdminTemplatePreview {
  html: string;
}

/**
 * `GET admin/template-options` response item — mirrors `AdminTemplateOption`
 * (redesign-panel-hubspot-mailgun, PR 9, task 9.1/9.2/9.3). Deliberately
 * narrower than `AdminTemplateSummary`: never carries `rutaBlob` (internal
 * blob storage path) or `temporada` — only the recognizable fields the
 * campaign editor's template selector needs (design.md §5.2: "product/
 * version/active state").
 */
export interface AdminTemplateOption {
  templateId: string;
  nombre: string;
  producto: string;
  version: number;
  activo: boolean;
}

/** `POST admin/templates` request body — mirrors `AdminTemplateCreateRequest`. */
export interface AdminTemplateCreateRequest {
  nombre: string;
  producto: string;
  temporada: string;
  html: string;
}

/** `PUT admin/templates/{id}/content` request body — content-only, never metadata (design.md A6) — mirrors `AdminTemplateContentRequest`. */
export interface AdminTemplateContentRequest {
  html: string;
}

/** `POST admin/templates` (201) / `PUT admin/templates/{id}/content` (200) response — mirrors `AdminTemplateWriteResult`. */
export interface AdminTemplateWriteResult {
  templateId: string;
  version: number;
  rutaBlob: string;
}

/**
 * `GET admin/templates/{id}/attachments` list item / `POST .../attachments` (201) response
 * — mirrors `AdminAttachmentSummary` (plantilla-a3-adjuntos-pdf design.md, Interfaces/Contracts).
 * Bound to the stable `templateId`, never to `version` (D3).
 */
export interface AdminAttachmentSummary {
  attachmentId: string;
  templateId: string;
  nombreArchivo: string;
  rutaBlob: string;
  mimeType: string;
  tamanoBytes: number;
  orden: number;
  createdAt: string;
}

/**
 * `POST admin/templates/{id}/attachments` request body — mirrors `AdminAttachmentUploadRequest`.
 * `contenidoBase64` never carries the `data:` URI prefix (design.md Data Flow).
 */
export interface AdminAttachmentUploadRequest {
  nombreArchivo: string;
  mimeType: string;
  contenidoBase64: string;
}

// ---------------------------------------------------------------------------
// Campaigns (`admin/campaigns...` — Services/Admin/AdminCampaignModels.cs, Services/CampaignDispatchService.cs)
// ---------------------------------------------------------------------------

/**
 * `GET admin/campaigns?...` list item — mirrors `AdminCampaignSummary`
 * (`AdminCampaignModels.cs`). PR7 (backend, redesign-panel-hubspot-mailgun)
 * extended the original 5 fields additively with recognizable template/
 * segment names and monitoring/capability metrics; the original 5 fields are
 * unchanged in name/type/JSON casing. `monitoring` carries the 3 `available`
 * metrics (`encolado`/`error` from `send_event`, `suprimidos` from
 * `segment-current-membership`); `capabilities` carries the 6 `unavailable`/
 * F2-01 metrics, identical across every row in one response (PR7's own
 * disclosed deviation: reuses `OperationalMetric`, not design.md's sample
 * `CapabilityStatus`, which was never implemented in this codebase).
 */
export interface AdminCampaignSummary {
  campaignId: string;
  nombre: string;
  asunto: string | null;
  estado: string;
  fechaProgramada: string;
  templateId: string;
  templateName: string;
  segmentId: string;
  segmentName: string;
  /**
   * issue #330 (frontend-only slice, campaign-list-sort-show-column): mirrors
   * the same Show join `GET admin/campaigns/{id}` already returns
   * (`AdminCampaignDetail.showName`) — not implemented on the LIST endpoint
   * yet, so every real response has these `undefined`/absent today. Built in
   * parallel so the UI is ready to render the moment the backend ships it;
   * `showName ?? "—"` degrades to the shared "data not available" convention
   * meanwhile.
   */
  showId: string | null;
  showName: string | null;
  /** Distinct recipients ACS accepted ("enviados") — drives the list/Inicio sending-pace bars. Absent on older API versions. */
  enviados?: number;
  monitoring: OperationalMetric[];
  capabilities: OperationalMetric[];
  /**
   * feat/campaign-batch-grouping: mirrors `AdminCampaignSummary.BatchId`
   * (backend, `Guid?`) — `null` for an individually-created campaign, shared
   * by every campaign born from the same `CreateBatchAsync` run. The
   * batch-grouping list UI (`CampaignListScreen`'s `buildCampaignDisplayRows`)
   * consumes this to collapse a same-`batchId` group into one expandable row.
   */
  batchId: string | null;
}

/** `GET admin/campaigns?...` response — mirrors `AdminCampaignPage`. */
export type AdminCampaignPage = AdminPage<AdminCampaignSummary>;

/** `GET admin/campaigns/{id}` response — mirrors `AdminCampaignDetail`. */
export interface AdminCampaignDetail {
  campaignId: string;
  nombre: string;
  asunto: string | null;
  templateId: string;
  templateName: string;
  segmentId: string;
  estado: string;
  fechaProgramada: string;
  createdBy: string;
  showName: string | null;
  /**
   * Codex review round 4 (P1, PR #384): non-null once this campaign's audience has been
   * fixed, whether by a batch creation (already fixed at creation time, even while
   * `estado` is still `"borrador"`) or by a prior schedule. Mirrors the backend's own
   * `audiencia_fijada_en IS NULL` guard on the audience-filter PUT
   * (`AdminAudienceFilterSqlBuilder.BuildUpsertAudienceFilterCommand`) — once this is
   * non-null, that PUT always 409s, so `CampaignDetailScreen` must never attempt it and
   * should disable the audience fields instead.
   */
  audienciaFijadaEn: string | null;
}

/**
 * `POST`/`PUT admin/campaigns[/{id}]` request body — mirrors `AdminCampaignDraft`.
 * `nombre` and `asunto` are two INDEPENDENT fields (admin-api "Campañas — borrador
 * con asunto propio") — never derive one from the other in the UI.
 */
export interface AdminCampaignDraft {
  nombre: string;
  asunto: string | null;
  templateId: string;
  segmentId: string;
  fechaProgramada: string;
}

/**
 * epic #236 tarea #241: `POST admin/campaigns` sin `segmentId` — mismos campos que
 * `AdminCampaignDraft` MENOS `segmentId` (nunca `""`: el campo debe estar AUSENTE del
 * JSON para que el backend lo trate como "sin Segmento", ver
 * `AdminCampaignsFunction.Create`). Un tipo separado, no un `segmentId` opcional en
 * `AdminCampaignDraft`, para que TypeScript nunca permita enviarlo por accidente.
 */
export interface AdminCampaignDraftWithoutSegment {
  nombre: string;
  asunto: string | null;
  templateId: string;
  fechaProgramada: string;
}

/** Query params accepted by `GET admin/campaigns` (mirrors `AdminCampaignQuery`). */
export interface AdminCampaignListParams {
  estado?: string;
  /** Campaign-name search (server-side ILIKE, trimmed, max 100 chars). */
  q?: string;
  page?: number;
  size?: number;
}

/** `POST admin/campaigns/{id}/schedule` request body — mirrors `ScheduleCampaignRequest`. */
export interface ScheduleCampaignRequest {
  fechaProgramada: string;
}

/**
 * `POST panel/campaigns/batch/{batchId}/schedule` response body — mirrors
 * `AdminCampaignBatchScheduleResult` (odd/tasks/campaign-batch-bulk-schedule.md
 * task 1). Each `'borrador'` sibling in the batch is scheduled with its own
 * already-computed `fechaProgramada` (continue-on-error: one failing/
 * ineligible sibling never blocks the rest).
 */
export interface ScheduleCampaignBatchResult {
  programadas: number;
  omitidas: number;
  fallidas: number;
}

/**
 * `PUT`/`GET panel/campaigns/{id}/audience-filter` request/response body —
 * mirrors `AdminAudienceFilterSelection` (epic #236 tarea #239, work unit
 * 239.6; `empresa` added by campaign-audience-filter-empresa;
 * `empresas` added by odd/tasks/campaign-audience-empresa-multiselect.md,
 * T2). `null` means "no restriction on this dimension", never a
 * missing/omitted key (backend always serializes every field). `cargo` is a
 * free-text ILIKE fragment against `contact.cargo` — NEVER compared to or
 * derived from `nivelJerarquico` (explicit acceptance criterion of issue
 * #239, same independence already enforced on the backend). `paisEmpresa`/
 * `categoria` are EXACT matches against `contact.empresa`'s related columns.
 *
 * `empresas` (string array, max 100 entries, each ≤200 chars per the
 * backend's `AdminAudienceFilterSelectionValidation`) is now the SOURCE OF
 * TRUTH for the Empresa restriction — `null`/`[]` means no restriction.
 * `empresa` is a DERIVED, backend-owned compat field (the single value when
 * `empresas` has exactly one entry, else `null`) — the panel's multi-select
 * UI (`EmpresaSelector`, T3) never reads it for a NEW save and always sends
 * `empresa: null` on PUT, letting `empresas` drive; it is still read when
 * HYDRATING a legacy saved filter that predates this feature (`empresas`
 * null, `empresa` a single string) — see `AudienceFilterEditor`.
 */
export interface AdminAudienceFilterSelection {
  showId: string | null;
  paisEmpresa: string | null;
  empresa: string | null;
  cargo: string | null;
  categoria: string | null;
  empresas: string[] | null;
}

/**
 * Query params accepted by `GET panel/campaign-audience-preview` (mirrors
 * `AdminAudienceFilterCriteria`, minus `client_id` which never travels from
 * the client per D3/ADR-005). Same combinable criteria as
 * `AdminAudienceFilterSelection` plus pagination — kept as a DISTINCT type
 * since `page`/`size` make no sense in what gets persisted per campaign.
 *
 * odd/tasks/campaign-audience-empresa-multiselect.md (T3): `empresa`
 * (scalar) is replaced by `empresas` (array, serialized as repeated
 * `?empresas=A&empresas=B` query params) — the panel never sends the legacy
 * scalar `empresa` for a preview request anymore; the backend still accepts
 * it from other/legacy callers, but that is out of scope for this type.
 */
export interface AdminAudienceFilterPreviewParams {
  showId?: string;
  paisEmpresa?: string;
  empresas?: string[];
  cargo?: string;
  categoria?: string;
  page?: number;
  size?: number;
}

/**
 * feat/campaign-batch-scheduling: `POST panel/campaigns/batch` request body —
 * mirrors `AdminCampaignBatchRequest`. Reparte la audiencia que matchea
 * `criterio` en N campañas de `tamanoLote` contactos cada una (el último
 * lote puede quedar más chico), cada una programada
 * `fechaInicio + índice * intervaloMinutos`. Decisión de producto (usuario,
 * confirmada): tamaño FIJO por lote, no cantidad fija de lotes.
 */
export interface AdminCampaignBatchRequest {
  nombre: string;
  asunto: string | null;
  templateId: string;
  criterio: AdminAudienceFilterSelection;
  tamanoLote: number;
  fechaInicio: string;
  intervaloMinutos: number;
}

/** Una campaña ya creada y programada como parte de un lote — mirrors `AdminCampaignBatchItem`. `contactos` es el conteo REAL fijado en su snapshot, no una estimación. */
export interface AdminCampaignBatchItem {
  campaignId: string;
  nombre: string;
  fechaProgramada: string;
  contactos: number;
}

/** `GET admin/campaigns/{id}/monitoring` response — mirrors `AdminCampaignMonitoring`. Live counts, not a dispatch-time snapshot. */
export interface AdminCampaignMonitoring {
  campaignId: string;
  estado: string;
  encolados: number;
  suprimidos: number;
  fallidos: number;
  /** Accepted by the mail provider, delivery report still awaited (< 48 h). Absent on older API versions. */
  pendientesConfirmacion?: number;
  /** Accepted over 48 h ago with no delivery report ("sin confirmación"). Absent on older API versions. */
  sinConfirmacion?: number;
  /** Distinct recipients the mail provider (ACS) accepted ("enviados") — drives the detail progress bar. Absent on older API versions. */
  enviados?: number;
  /** Instant the first email was sent (ISO-8601), `null` when none yet. Absent on older API versions. */
  primerEnvioEn?: string | null;
  /** Instant the last email so far was sent (ISO-8601), `null` when none yet. Absent on older API versions. */
  ultimoEnvioEn?: string | null;
  /** Instant the campaign reached a final state (ISO-8601); `null` while sending and for campaigns finished before it was recorded. Absent on older API versions. */
  completadaEn?: string | null;
}

/**
 * `POST admin/campaigns/{id}/dispatch` response (202) — async-campaign-dispatch (Option A,
 * native review correction PR #311): the endpoint is fire-and-forget now (AdminCampaignsFunction.
 * Dispatch enqueues and returns immediately), so the response is just the acknowledged campaign
 * id, not a `CampaignDispatchOutcome`-shaped per-contact count (that loop now runs later, in the
 * background `CampaignDispatchQueueFunction` consumer).
 */
export interface AdminCampaignDispatchAcknowledgement {
  campaignId: string;
}

// ---------------------------------------------------------------------------
// Telemetry (`admin/campaigns/{id}/telemetry` — Services/Admin/TelemetryCapability.cs, AdminTelemetryService.cs)
// ---------------------------------------------------------------------------

/** One `send_event.tipo` not implemented yet (F2-01) — mirrors `TelemetryPendingType`. */
export interface TelemetryPendingType {
  tipo: string;
  bloqueadoPor: string;
}

/**
 * `GET admin/campaigns/{id}/telemetry` response — mirrors `CampaignTelemetry`.
 * The graceful-degradation contract (design.md): `tiposPendientes` is
 * server-owned and MUST render as an explicit pending row, never as a `0`
 * indistinguishable from "no events yet" (`admin-panel-ui` "Comunicación del
 * estado 'pendiente F2-01'").
 *
 * redesign-panel-hubspot-mailgun, PR 10 (task 10.5/10.6): `generatedAt`/
 * `metrics` are additive (backend PR 10 slice 1, `AdminTelemetryService`).
 * `metrics` is the SAME `OperationalMetric[]` shape the overview's capability
 * register already consumes (2 `available` — `encolado`/`error`, real
 * aggregate counts including measured zero — plus the 6 `unavailable`/F2-01
 * entries) — the redesigned telemetry screens render from `metrics`, never
 * from `eventos`/`tiposDisponibles`/`tiposPendientes` directly; those 3
 * original fields remain only for previous-SPA rollback compatibility
 * (design.md "Additive read-model rollout and rollback compatibility").
 */
export interface CampaignTelemetry {
  campaignId: string;
  eventos: Record<string, number>;
  tiposDisponibles: string[];
  tiposPendientes: TelemetryPendingType[];
  generatedAt: string;
  metrics: OperationalMetric[];
}

// ---------------------------------------------------------------------------
// Operational health (`admin/panel/operational-health` — Services/Admin/AdminOperationalHealthModels.cs)
// ---------------------------------------------------------------------------

/**
 * Closed status vocabulary shared by every status field below — mirrors the
 * backend's `OperationalHealthStatus` (design.md §8: "closed constants...
 * validated in constructors/tests"). A THIRD state ("degraded") that
 * `MetricAvailability`'s 2-state available/unavailable vocabulary cannot
 * express (redesign-panel-hubspot-mailgun, PR 11, task 11.2).
 */
export type OperationalHealthStatus = "available" | "degraded" | "pending";

/** Mirrors `IntegrationStatus.VerificationKind` — how weak/strong the evidence behind a `status` is, never itself a live probe. */
export type VerificationKind = "request" | "query" | "configuration";

/** Mirrors `TenantIdentity` (design.md §5.7 item 1 "Identidad"): safe tenant ID/display name, no tenant switch. */
export interface TenantIdentity {
  clientId: string;
  displayName: string;
  status: OperationalHealthStatus;
}

/** Mirrors `CapabilityStatus` (design.md §5.7 item 2 "Capacidades") — reuses the same F2-01 keys `metricLabel` already localizes. */
export interface CapabilityStatus {
  key: string;
  status: OperationalHealthStatus;
  blockedBy: string | null;
  evidence: string;
}

/** Mirrors `IntegrationStatus` (design.md §5.7 item 3 "Integraciones"). `guidanceCode` is a closed code, never free text — the SPA owns the actual Spanish copy (`features/operational-health/copy.ts`), same separation `blockedBy`/F2-01 already established. */
export interface IntegrationStatus {
  key: string;
  status: OperationalHealthStatus;
  verificationKind: VerificationKind;
  checkedAt: string;
  guidanceCode: string;
}

/**
 * `GET panel/operational-health` response — mirrors `AdminOperationalHealth`.
 * No client-addressable tenant selector (same non-disclosing contract as
 * `AdminOverview`): a supplied `client_id` is structurally ignored.
 */
export interface AdminOperationalHealth {
  generatedAt: string;
  identity: TenantIdentity;
  capabilities: CapabilityStatus[];
  integrations: IntegrationStatus[];
}
